import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '../config';
import { prepareHeaders } from './helpers';
import { ServiceTypes } from '../constants';
import { createBaseQueryWithReauth } from './baseQueryWithReauth';
import { AppDispatch } from '../store';
import { userRolesMap } from '@clatter/platform';

const mapUsersByClatterId = (users: User[]) => {
  return users.reduce<UsersByClatterIdMap>((acc, user) => {
    acc[user.id] = user;
    return acc;
  }, {});
};

const mapUsersByEmail = (users: User[]) => {
  return users.reduce<UsersByEmailMap>((acc, user) => {
    acc[user.email] = [
      {
        ...user,
        _id: user?.id,
      },
    ];
    return acc;
  }, {});
};

const usersApi = createApi({
  reducerPath: 'usersApi',
  keepUnusedDataFor: config.rtkCacheTimeout,
  baseQuery: createBaseQueryWithReauth(
    fetchBaseQuery({
      baseUrl: config.apiV2BaseUrl,
      prepareHeaders: prepareHeaders,
    }),
  ),
  tagTypes: [ServiceTypes.USERS],
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) => result.data,
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST' }],
    }),
    getProvisionedUsers: builder.query<User[], void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) =>
        result.data.filter((user) =>
          user.roles?.some((r) => r.role?.name === userRolesMap.ngAdmin || r.role?.name === userRolesMap.ngUser),
        ),
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST' }],
    }),
    getUsersByClatterId: builder.query<UsersByClatterIdMap, void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) => mapUsersByClatterId(result.data),
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST_BY_CLATTER_ID' }],
    }),
    getUsersByEmail: builder.query<UsersByEmailMap, void>({
      query: () => `/user`,
      transformResponse: (result: IApiV2Response<User>) => mapUsersByEmail(result.data),
      providesTags: () => [{ type: ServiceTypes.USERS, id: 'LIST_BY_EMAIL' }],
    }),
  }),
});

interface UsersApiCustomHelpers {
  invalidateAllTags: () => (dispatch: AppDispatch) => ReturnType<typeof usersApi.util.invalidateTags>;
}

type UsersApiWithCustom = typeof usersApi & {
  custom: UsersApiCustomHelpers;
};

const usersApiWithCustom: UsersApiWithCustom = Object.assign({}, usersApi, {
  custom: {
    invalidateAllTags: () => (dispatch: AppDispatch) => dispatch(usersApi.util.invalidateTags([ServiceTypes.USERS])),
  },
});

export { usersApiWithCustom as usersApi };

export const { useGetUsersQuery, useGetProvisionedUsersQuery, useGetUsersByEmailQuery, useGetUsersByClatterIdQuery } =
  usersApi;
